import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Edit2, Trash2, CheckCircle, AlertCircle, Loader2, Link as LinkIcon, Calendar, Users, Eye } from 'lucide-react';
import { authenticatedFetch, getUser } from '@/lib/auth';
import { useConfirm } from './ui';

interface CourseInstance {
  id: string;
  name: string;
  exam_set_id: string;
  exam_title: string;
  study_plan_id: string;
  study_plan_title: string;
  ects_credits: number;
  member_id: string;
  organization_name: string;
  api_link: string | null;
  api_enabled: boolean;
  start_date: string | null;
  end_date: string | null;
  max_candidates: number | null;
  status: string;
  created_at: string;
  session_count: number;
}

interface ExamSet {
  id: string;
  title: string;
  status: string;
  study_plan_id?: string;
  study_plan_title?: string;
  ects_credits?: number;
}

export default function CourseInstanceManagement() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { confirm } = useConfirm();
  const [instances, setInstances] = useState<CourseInstance[]>([]);
  const [examSets, setExamSets] = useState<ExamSet[]>([]);
  const [members, setMembers] = useState<{ id: string; organization_name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Get current user info for role-based filtering
  const user = getUser();
  const isMemberUser = user?.role === 'member';
  const userMemberId = user?.member_id;

  const [formData, setFormData] = useState({
    name: '',
    exam_set_id: '',
    member_id: '',
    api_link: '',
    api_enabled: true,
    start_date: '',
    end_date: '',
    max_candidates: '',
    status: 'active'
  });

  // Auto-set member_id for member users on mount
  useEffect(() => {
    if (isMemberUser && userMemberId) {
      setFormData(prev => ({ ...prev, member_id: userMemberId }));
    }
  }, [isMemberUser, userMemberId]);

  // Check for exam_set_id query param (from ExamDetail "Opprett kursinstans" button)
  useEffect(() => {
    const examSetId = searchParams.get('exam_set_id');
    if (examSetId && examSets.length > 0) {
      // Pre-fill form with selected exam set
      const selectedExam = examSets.find(e => e.id === examSetId);
      if (selectedExam) {
        setFormData(prev => ({
          ...prev,
          exam_set_id: examSetId,
          name: selectedExam.title,
          // Auto-set member_id for member users
          member_id: isMemberUser && userMemberId ? userMemberId : prev.member_id
        }));
        setShowForm(true);
        // Clear the query param to avoid re-opening on refresh
        setSearchParams({});
      }
    }
  }, [searchParams, examSets, isMemberUser, userMemberId]);

  useEffect(() => {
    fetchInstances();
    fetchExamSets();
    fetchMembers();
  }, []);

  const fetchInstances = async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch('/api/admin/course-instances');
      const data = await response.json();

      if (data.success) {
        setInstances(data.instances);
      } else {
        setError(data.message || 'Kunne ikke laste kursinstanser');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchExamSets = async () => {
    try {
      const response = await authenticatedFetch('/api/exams/list');
      const data = await response.json();
      if (data.success) {
        // Show all exam sets, not just published - admins may want to set up course instances early
        // Sort by status (published first) then by title
        const sortedExams = data.exam_sets.sort((a: ExamSet, b: ExamSet) => {
          if (a.status === 'published' && b.status !== 'published') return -1;
          if (a.status !== 'published' && b.status === 'published') return 1;
          return (a.title || '').localeCompare(b.title || '');
        });
        setExamSets(sortedExams);
      }
    } catch (err) {
      console.error('Failed to fetch exam sets:', err);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await authenticatedFetch('/api/admin/members');
      const data = await response.json();
      if (data.success) {
        setMembers(data.members);
      }
    } catch (err) {
      console.error('Failed to fetch members:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const payload = {
        ...formData,
        max_candidates: formData.max_candidates ? parseInt(formData.max_candidates) : null,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null
      };

      const url = editingId
        ? `/api/admin/course-instances/${editingId}`
        : '/api/admin/course-instances';
      const method = editingId ? 'PATCH' : 'POST';

      const response = await authenticatedFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(editingId ? 'Kursinstans oppdatert' : 'Kursinstans opprettet');
        setShowForm(false);
        setEditingId(null);
        resetForm();
        fetchInstances();
      } else {
        setError(data.message || 'Kunne ikke lagre kursinstans');
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleEdit = (instance: CourseInstance) => {
    setEditingId(instance.id);

    // Format dates for input type="date" (requires YYYY-MM-DD format)
    const formatDate = (date: string | null) => {
      if (!date) return '';
      // If date includes time (ISO format), extract just the date part
      return date.split('T')[0];
    };

    setFormData({
      name: instance.name,
      exam_set_id: instance.exam_set_id,
      member_id: instance.member_id,
      api_link: instance.api_link || '',
      api_enabled: instance.api_enabled,
      start_date: formatDate(instance.start_date),
      end_date: formatDate(instance.end_date),
      max_candidates: instance.max_candidates?.toString() || '',
      status: instance.status
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    const confirmed = await confirm({
      title: 'Slett kursinstans',
      message: 'Er du sikker på at du vil slette denne kursinstansen? Dette vil også slette alle tilhørende prøvesesjoner.',
      confirmLabel: 'Slett',
      cancelLabel: 'Avbryt',
      variant: 'danger'
    });

    if (!confirmed) return;

    try {
      setError('');
      const response = await authenticatedFetch(`/api/admin/course-instances/${id}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Kursinstans slettet');
        fetchInstances();
      } else {
        setError(data.message || 'Kunne ikke slette kursinstans');
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      exam_set_id: '',
      member_id: isMemberUser && userMemberId ? userMemberId : '',
      api_link: '',
      api_enabled: true,
      start_date: '',
      end_date: '',
      max_candidates: '',
      status: 'active'
    });
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    resetForm();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Kursinstanser</h2>
          <p className="text-sm text-gray-600 mt-1">
            Administrer avsluttende prøver og kursgjennomføringer
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowForm(!showForm);
            setEditingId(null);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-5 w-5" />
          Ny kursinstans
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center gap-2">
          <CheckCircle className="h-5 w-5" />
          <span>{success}</span>
        </div>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white border rounded-lg p-6 space-y-4">
          <h3 className="text-lg font-semibold">
            {editingId ? 'Rediger kursinstans' : 'Opprett kursinstans'}
          </h3>

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Instansnavn *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="f.eks. Geopolitikk - Vår 2025"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prøvesett *
              </label>
              <select
                required
                value={formData.exam_set_id}
                onChange={(e) => setFormData({ ...formData, exam_set_id: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Velg prøvesett...</option>
                {examSets.map(exam => (
                  <option key={exam.id} value={exam.id}>
                    {exam.status !== 'published' ? `[${exam.status === 'draft' ? 'Utkast' : exam.status}] ` : ''}
                    {exam.study_plan_title ? `${exam.study_plan_title} → ${exam.title}` : exam.title}
                    {exam.ects_credits ? ` (${exam.ects_credits} KP)` : ''}
                  </option>
                ))}
              </select>
              {examSets.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  Ingen prøvesett funnet. Opprett et prøvesett i Prøvesystem-fanen først.
                </p>
              )}
            </div>

            {/* Hide member dropdown for member users - auto-set from their profile */}
            {!isMemberUser && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Medlemsorganisasjon *
                </label>
                <select
                  required
                  value={formData.member_id}
                  onChange={(e) => setFormData({ ...formData, member_id: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Velg organisasjon...</option>
                  {members.map(member => (
                    <option key={member.id} value={member.id}>
                      {member.organization_name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {isMemberUser && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Din organisasjon
                </label>
                <div className="w-full px-3 py-2 border rounded-lg bg-gray-50 text-gray-700">
                  {members.find(m => m.id === userMemberId)?.organization_name || 'Laster...'}
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                API-lenke (unik identifikator)
              </label>
              <input
                type="text"
                value={formData.api_link}
                onChange={(e) => setFormData({ ...formData, api_link: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., geopolitikk-var-2025"
              />
              <p className="text-xs text-gray-500 mt-1">
                Brukes i API-endepunkter: /api/v1/exam/start
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Maks antall kandidater
              </label>
              <input
                type="number"
                min="1"
                value={formData.max_candidates}
                onChange={(e) => setFormData({ ...formData, max_candidates: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="La stå tom for ubegrenset"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Startdato
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sluttdato
              </label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="active">Aktiv</option>
                <option value="completed">Fullført</option>
                <option value="cancelled">Kansellert</option>
              </select>
            </div>

            <div className="col-span-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.api_enabled}
                  onChange={(e) => setFormData({ ...formData, api_enabled: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <span className="text-sm font-medium text-gray-700">
                  Aktiver API-tilgang for dette kurset
                </span>
              </label>
            </div>
          </div>

          <div className="flex gap-2 pt-4">
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {editingId ? 'Oppdater' : 'Opprett'} kursinstans
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              Avbryt
            </button>
          </div>
        </form>
      )}

      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Instans / Prøve
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Organisasjon
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                API-lenke
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Periode
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Økter
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Handlinger
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {instances.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                  Ingen kursinstanser funnet. Opprett en for å komme i gang.
                </td>
              </tr>
            ) : (
              instances.map((instance) => (
                <tr key={instance.id}>
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-gray-900">{instance.name}</div>
                      <div className="text-sm text-gray-500">
                        📚 {instance.study_plan_title}
                      </div>
                      <div className="text-xs text-gray-400">
                        {instance.exam_title} • {instance.ects_credits} KP
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {instance.organization_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {instance.api_link ? (
                      <div className="flex items-center gap-1">
                        <LinkIcon className="h-3 w-3 text-gray-400" />
                        <code className="text-xs bg-gray-50 px-2 py-1 rounded">
                          {instance.api_link}
                        </code>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {instance.start_date ? (
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        <span>
                          {new Date(instance.start_date).toLocaleDateString()} -{' '}
                          {instance.end_date
                            ? new Date(instance.end_date).toLocaleDateString()
                            : '?'}
                        </span>
                      </div>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      <span>
                        {instance.session_count}
                        {instance.max_candidates ? `/${instance.max_candidates}` : ''}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        instance.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : instance.status === 'completed'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {instance.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => navigate(`/course-instances/${instance.id}`)}
                        className="text-[#B5651D] hover:text-[#8B4513]"
                        title="Se detaljer"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(instance)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Rediger"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(instance.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Slett"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
